// Relation-KIND filtering for PostgreSQL's `<schema>.▮` DDL drills.
//
// `isPgsqlRelationOnlyDrill` (pgsqlExtras) answers a different question — may
// a ROUTINE appear here? — from a one-token deny-list in front of the
// qualifier. That token cannot separate `ALTER TABLE public.▮` (base tables
// only) from `COMMENT ON TABLE public.▮` (tables, views AND matviews are all
// legal), because both read `TABLE .` in front of the caret. The statement's
// LEADING keywords are what decide, so this classifier reads those instead.
//
// Which relkinds each statement admits, per the PG grammar:
//   TRUNCATE …                     → tables (a view/matview is a syntax error)
//   ALTER TABLE …                  → tables (matview has ALTER MATERIALIZED VIEW)
//   DROP TABLE …                   → tables
//   DROP VIEW …                    → views
//   ALTER MATERIALIZED VIEW …      → matviews
//   DROP MATERIALIZED VIEW …       → matviews
//   REFRESH MATERIALIZED VIEW …    → matviews
// Everything else (`COMMENT ON TABLE`, `INSERT INTO`, `SELECT … FROM`, …)
// returns `null` — no filtering, today's behaviour.

import { tokenize, type Token } from '../context/tokenizer';
import { stripCommentsPreserving } from '../context/sqlClauseToPositionKind';

const ENGINE = 'pgsql' as const;

/** The relation kinds a drill admits, in `TableMeta['kind']` terms. */
export type PgDrillRelationKind = 'table' | 'view' | 'materialized-view';

function identUpper(t: Token | undefined): string | null {
  if (!t || (t.type !== 'ident' && t.type !== 'kw')) return null;
  return t.text.toUpperCase();
}

/**
 * The single relation kind this statement's object reference admits, or
 * `null` when the statement puts no kind restriction on it.
 */
export function classifyPgRelationDrillKind(
  source: string,
  statementStart: number,
  cursor: number,
): PgDrillRelationKind | null {
  const tokens = tokenize(source, statementStart, cursor, ENGINE);
  const t0 = identUpper(tokens[0]);
  if (!t0) return null;
  const t1 = identUpper(tokens[1]);
  const t2 = identUpper(tokens[2]);

  if (t0 === 'TRUNCATE') return 'table';
  if (t0 === 'REFRESH') {
    return t1 === 'MATERIALIZED' && t2 === 'VIEW' ? 'materialized-view' : null;
  }
  if (t0 === 'ALTER' || t0 === 'DROP') {
    if (t1 === 'TABLE') return 'table';
    if (t1 === 'VIEW') return 'view';
    if (t1 === 'MATERIALIZED' && t2 === 'VIEW') return 'materialized-view';
    return null;
  }
  return null;
}

export type BufferMatview = {
  readonly name: string;
  readonly schema: string | null;
};

// `IF NOT EXISTS` is optional; `CREATE OR REPLACE` is not legal for a matview
// so it is deliberately absent.
const CREATE_MATVIEW_RE =
  /\bCREATE\s+MATERIALIZED\s+VIEW\s+(?:IF\s+NOT\s+EXISTS\s+)?((?:"[^"]*"|[A-Za-z_][\w$]*)(?:\.(?:"[^"]*"|[A-Za-z_][\w$]*))*)/gi;

/**
 * Every `CREATE MATERIALIZED VIEW <name>` in the buffer.
 *
 * A matview the script itself creates cannot be in the schema snapshot — its
 * DDL has not executed yet — and `REFRESH MATERIALIZED VIEW public.▮` three
 * lines later is exactly the caret that needs it (pgsql script.32 §7). Same
 * fire-and-forget contract as `scanBufferCreatedSequences` /
 * `scanBufferCreatedRoutines`: the whole buffer is scanned (a matview created
 * above OR below the caret is a likely completion) and no attempt is made to
 * replay intervening DROPs.
 */
export function scanBufferCreatedMatviews(text: string): BufferMatview[] {
  const out: BufferMatview[] = [];
  const seen = new Set<string>();
  const stripped = stripCommentsPreserving(text);
  let m: RegExpExecArray | null;
  CREATE_MATVIEW_RE.lastIndex = 0;
  while ((m = CREATE_MATVIEW_RE.exec(stripped)) !== null) {
    const parts = m[1]
      .split('.')
      .map((p) => (p.startsWith('"') && p.endsWith('"') ? p.slice(1, -1).replace(/""/g, '"') : p));
    const name = parts[parts.length - 1] ?? '';
    if (!name) continue;
    const schema = parts.length >= 2 ? (parts[parts.length - 2] || null) : null;
    const key = `${(schema ?? '').toLowerCase()}|${name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name, schema });
  }
  return out;
}
